// Document parsers — pull text + structured metadata from common file formats.
//
// All parsers run client-side in the Tauri WebKit. Heavy libraries (pdfjs,
// xlsx, mammoth) are dynamically imported so they don't bloat the initial
// bundle — only paid for when a doc of that format is dropped.
//
// Output shape is the same regardless of format: a `ParsedDocument` with the
// extracted text (chunkable for RAG), a per-section breakdown (sheets / pages
// / slides), and lightweight metadata (file name, type, size, pulled at).

export type DocumentKind =
  | "xlsx"
  | "csv"
  | "docx"
  | "pdf"
  | "pptx"
  | "html"
  | "txt"
  | "image"
  | "unknown";

export interface ParsedSection {
  /** "Sheet: Action Tracker", "Page 3", "Slide 5", etc. */
  label: string;
  /** Section's plain text. */
  text: string;
}

/** Optional image payload — set when `kind === "image"`. Phase 3 will ferry
 * this to Claude's vision endpoint so the LLM can "read" the picture. */
export interface ImagePayload {
  /** Full data URL: "data:image/png;base64,…". */
  dataUrl: string;
  /** Pixel dimensions reported by the browser. 0/0 if probing failed. */
  width: number;
  height: number;
  /** MIME type from the File API. */
  mimeType: string;
}

export interface ParsedDocument {
  id: string;
  fileName: string;
  kind: DocumentKind;
  /** Total characters in `text` — quick size signal. */
  charCount: number;
  /** Sections concatenated with double-newlines between. Use this for chunking. */
  text: string;
  /** Per-sheet / per-page / per-slide breakdown for citations. */
  sections: ParsedSection[];
  /** ISO timestamp of parse. */
  pulledAt: string;
  /** Original file size in bytes. */
  bytes: number;
  /** Set for image documents only. */
  image?: ImagePayload;
}

// ---------------------------------------------------------------------------

export function detectKind(fileName: string): DocumentKind {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".xlsx") || lower.endsWith(".xls") || lower.endsWith(".xlsm")) return "xlsx";
  if (lower.endsWith(".csv") || lower.endsWith(".tsv")) return "csv";
  if (lower.endsWith(".docx")) return "docx";
  if (lower.endsWith(".pdf")) return "pdf";
  if (lower.endsWith(".pptx")) return "pptx";
  if (lower.endsWith(".html") || lower.endsWith(".htm")) return "html";
  if (lower.endsWith(".txt") || lower.endsWith(".md")) return "txt";
  if (
    lower.endsWith(".png") ||
    lower.endsWith(".jpg") ||
    lower.endsWith(".jpeg") ||
    lower.endsWith(".gif") ||
    lower.endsWith(".webp")
  ) {
    return "image";
  }
  return "unknown";
}

function newId(): string {
  return "d_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 7);
}

/**
 * Parse a file. Throws on unsupported formats. Caller wraps in try/catch.
 */
export async function parseDocument(file: File): Promise<ParsedDocument> {
  const kind = detectKind(file.name);
  const base = {
    id: newId(),
    fileName: file.name,
    kind,
    pulledAt: new Date().toISOString(),
    bytes: file.size,
  };

  let sections: ParsedSection[];
  let image: ImagePayload | undefined;
  switch (kind) {
    case "xlsx":
      sections = await parseXlsx(file);
      break;
    case "csv":
      sections = await parseCsv(file);
      break;
    case "docx":
      sections = await parseDocx(file);
      break;
    case "pdf":
      sections = await parsePdf(file);
      break;
    case "pptx":
      sections = await parsePptx(file);
      break;
    case "html":
      sections = await parseHtml(file);
      break;
    case "txt":
      sections = await parseTxt(file);
      break;
    case "image": {
      const result = await parseImage(file);
      sections = result.sections;
      image = result.image;
      break;
    }
    default:
      throw new Error(`Unsupported file type: ${file.name}`);
  }

  const text = sections.map((s) => `## ${s.label}\n\n${s.text}`).join("\n\n");
  return { ...base, sections, text, charCount: text.length, image };
}

// ---- per-format parsers ---------------------------------------------------

async function parseXlsx(file: File): Promise<ParsedSection[]> {
  const XLSX = await import("xlsx");
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  return wb.SheetNames.map((name) => {
    const sheet = wb.Sheets[name];
    if (!sheet) return { label: `Sheet · ${name}`, text: "" };
    // CSV-ish text representation; preserves cell layout for the LLM.
    const csv = XLSX.utils.sheet_to_csv(sheet, { blankrows: false });
    return { label: `Sheet · ${name}`, text: csv };
  });
}

async function parseCsv(file: File): Promise<ParsedSection[]> {
  const text = await file.text();
  return [{ label: file.name, text }];
}

async function parseDocx(file: File): Promise<ParsedSection[]> {
  const mammoth = await import("mammoth");
  const buf = await file.arrayBuffer();
  // extractRawText is simpler and more reliable than html for RAG use.
  const result = await mammoth.extractRawText({ arrayBuffer: buf });
  return [{ label: file.name, text: result.value }];
}

// Cache the Blob URL across PDF parses — building it once means a single
// browser-resident Blob serves every subsequent `getDocument` call.
let pdfWorkerBlobUrl: string | null = null;

async function parsePdf(file: File): Promise<ParsedSection[]> {
  // We're on pdfjs-dist **3.11.174** (UMD build) — the same version the
  // diagnostic deck (bvndle-tags-diagnostic-deck.html) uses successfully.
  //
  // Why not the latest pdfjs (5.x)? Because v5 rewrote `getTextContent` to
  // return its results via a `ReadableStream` over postMessage. That stream
  // relies on structured-clone Transferable objects across the worker
  // boundary — fine in stock Chrome, broken in Tauri's WebKit when the
  // worker is loaded from a Blob URL. The doc-level operations (getDocument,
  // getPage) work because they're atomic postMessage calls; the moment we
  // touch the streaming text path, the stream resolves to `undefined` and
  // pdf.js's internal `for…of` over the chunks throws the now-familiar
  // "undefined is not a function (near '…i of e…')". Confirmed by the
  // stack trace pointing inside `getTextContent`. Diagnosis: WebKit + Blob
  // worker + streamed postMessage = unhappy.
  //
  // pdfjs 3.x's `getTextContent` returns a plain object synchronously from
  // the worker — no streaming, no Transferables, no problem. Same Blob URL
  // worker trick from the diagnostic deck, just with the older library.
  const pdfjs = await import("pdfjs-dist/legacy/build/pdf.js");

  if (!pdfWorkerBlobUrl) {
    try {
      const { default: workerAssetUrl } = await import(
        "pdfjs-dist/legacy/build/pdf.worker.min.js?url"
      );
      console.log("[knowledge] pdf worker asset URL:", workerAssetUrl);
      const res = await fetch(workerAssetUrl);
      if (!res.ok) {
        throw new Error(
          `Could not fetch pdf worker (${res.status} ${res.statusText})`,
        );
      }
      const workerSource = await res.text();
      const blob = new Blob([workerSource], { type: "application/javascript" });
      pdfWorkerBlobUrl = URL.createObjectURL(blob);
      console.log(
        "[knowledge] pdf worker blob URL ready · source size:",
        workerSource.length,
        "chars",
      );
    } catch (e) {
      console.error("[knowledge] pdf worker bootstrap failed", e);
      throw new Error(
        `Failed to bootstrap the PDF worker: ${
          e instanceof Error ? e.message : String(e)
        }`,
      );
    }
  }
  pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerBlobUrl;

  const buf = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buf }).promise;
  console.log("[knowledge] pdf loaded:", file.name, "pages:", doc.numPages);
  const sections: ParsedSection[] = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const text = content.items
      .map((item) => ("str" in item ? (item as { str: string }).str : ""))
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
    sections.push({ label: `Page ${i}`, text });
  }
  return sections;
}

async function parsePptx(file: File): Promise<ParsedSection[]> {
  // PPTX is a zip of XML. We unzip and pull text out of each slide's XML.
  // Lightweight implementation — no extra deps needed (JSZip would be cleaner
  // but adds bundle size; using native browser DecompressionStream + zip
  // walk via the xlsx package's `cfb` is overkill here).
  // For v1, use a JSZip dynamic import if it's already a transitive dep.
  // If JSZip isn't available, fall back to a friendly error.
  try {
    const JSZipModule = await import("jszip");
    const JSZip = JSZipModule.default ?? JSZipModule;
    const buf = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(buf);
    const slideFiles = Object.keys(zip.files)
      .filter((p) => /^ppt\/slides\/slide\d+\.xml$/i.test(p))
      .sort((a, b) => {
        const an = parseInt(a.match(/slide(\d+)/i)?.[1] ?? "0", 10);
        const bn = parseInt(b.match(/slide(\d+)/i)?.[1] ?? "0", 10);
        return an - bn;
      });
    const sections: ParsedSection[] = [];
    for (let i = 0; i < slideFiles.length; i++) {
      const path = slideFiles[i]!;
      const xml = await zip.files[path]!.async("string");
      // Strip XML tags; rough but works for slide text content.
      const text = xml
        .replace(/<a:p[^>]*>/g, "\n")
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      sections.push({ label: `Slide ${i + 1}`, text });
    }
    return sections;
  } catch {
    throw new Error(
      "PPTX parsing requires the jszip package. Run `npm install jszip` and retry.",
    );
  }
}

async function parseHtml(file: File): Promise<ParsedSection[]> {
  // HTML lives directly in the WebKit — DOMParser is native, no deps.
  // We strip <script>/<style> outright, then walk: title → headings → body.
  // Heading boundaries become their own sections so the LLM gets structure
  // back when chunking.
  const raw = await file.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(raw, "text/html");

  // Nuke noise.
  doc.querySelectorAll("script, style, noscript").forEach((n) => n.remove());

  const sections: ParsedSection[] = [];

  const title = doc.querySelector("title")?.textContent?.trim();
  if (title) {
    sections.push({ label: "Title", text: title });
  }

  // Walk top-level content. Use h1/h2/h3 as section boundaries.
  const body = doc.body ?? doc.documentElement;
  const headings = Array.from(body.querySelectorAll("h1, h2, h3"));

  if (headings.length === 0) {
    // No headings — emit the whole body as one section.
    const text = (body.textContent ?? "").replace(/\s+/g, " ").trim();
    sections.push({ label: file.name, text });
    return sections;
  }

  // For each heading, collect text until the next same-or-higher-level heading.
  for (let i = 0; i < headings.length; i++) {
    const head = headings[i]!;
    const label = (head.textContent ?? `Section ${i + 1}`).replace(/\s+/g, " ").trim();
    const parts: string[] = [];
    let node: Element | null = head.nextElementSibling;
    while (node && !/^H[1-3]$/i.test(node.tagName)) {
      const t = (node.textContent ?? "").replace(/\s+/g, " ").trim();
      if (t) parts.push(t);
      node = node.nextElementSibling;
    }
    sections.push({ label, text: parts.join("\n") });
  }
  return sections;
}

async function parseTxt(file: File): Promise<ParsedSection[]> {
  const text = await file.text();
  return [{ label: file.name, text }];
}

async function parseImage(
  file: File,
): Promise<{ sections: ParsedSection[]; image: ImagePayload }> {
  // Phase 1 for images: we don't OCR (Tesseract would add ~10MB to the
  // bundle for marginal value on the kind of docs the team drops). Instead
  // we stage the image:
  //   - Read it as a data URL so it's portable + serialisable to the Worker
  //     in Phase 2 without a separate binary upload path.
  //   - Probe pixel dimensions via the Image API.
  //   - Emit a placeholder text section so the doc shows up in the list with
  //     a sensible label. Phase 3 will replace this with Claude's vision
  //     description when the user asks a question that touches the image.
  const dataUrl = await readFileAsDataURL(file);
  const { width, height } = await probeImageDimensions(dataUrl);

  const summary = [
    `[Image asset awaiting Phase 3 vision analysis]`,
    `File: ${file.name}`,
    `Type: ${file.type || "image/*"}`,
    `Dimensions: ${width}×${height} px`,
    `Size: ${file.size.toLocaleString()} bytes`,
  ].join("\n");

  return {
    sections: [{ label: file.name, text: summary }],
    image: {
      dataUrl,
      width,
      height,
      mimeType: file.type || "application/octet-stream",
    },
  };
}

function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") {
        resolve(result);
      } else {
        reject(new Error("FileReader returned a non-string result"));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
    reader.readAsDataURL(file);
  });
}

function probeImageDimensions(
  dataUrl: string,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = dataUrl;
  });
}
